'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ChevronDown,
  LogOut,
  Menu,
  MessageSquare,
  Settings,
  User,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { BrandLogo } from '@/components/brand/BrandLogo';
import { useAuth } from '@/lib/AuthContext';
import { EASE_PREMIUM, fadeUp, staggerContainer } from '@/lib/motion';
import { cn } from '@/lib/utils';

const navLinks = [
  { label: 'Accueil', href: '/' },
  { label: 'Acheter', href: '/acheter' },
  { label: 'Louer', href: '/louer' },
  { label: 'Demande personnalisée', href: '/demande-personnalisee' },
  { label: 'Blog', href: '/blog' },
  { label: 'Publier mes annonces', href: '/nous-confier-un-bien' },
  { label: 'À propos', href: '/a-propos' },
];

/**
 * Editorial top navigation.
 * - Transparent over hero on `/`, becomes solid (cream + blur + border) on scroll.
 * - All other routes start solid immediately.
 * - Mobile: full-screen navy overlay with staggered reveal.
 */
export function Navbar() {
  const pathname = usePathname();
  const startsTransparent = pathname === '/';
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { user, logout } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setUserMenuOpen(false);
  }, [pathname]);

  const solid = !startsTransparent || scrolled;
  const initials = user
    ? `${user.firstName?.[0] ?? ''}${user.lastName?.[0] ?? ''}`.toUpperCase()
    : '';

  return (
    <>
      <header
        data-solid={solid ? 'true' : 'false'}
        className={cn(
          'fixed top-0 inset-x-0 z-50 transition-all duration-400 ease-premium',
          solid
            ? 'bg-surface/90 backdrop-blur-md border-b border-border-subtle shadow-nav'
            : 'bg-transparent border-b border-transparent',
        )}
      >
        {/* 3-column grid: logo | centered nav | actions */}
        <div className="container-page grid grid-cols-[auto_1fr_auto] h-[72px] items-center gap-4">

          {/* ── Logo ── */}
          <Link href="/" aria-label="Accueil TERRAMEZ" className="shrink-0">
            <BrandLogo variant={solid ? 'dark' : 'light'} size="md" />
          </Link>

          {/* ── Centered nav ── */}
          <nav
            aria-label="Navigation principale"
            className="hidden lg:flex items-center justify-center gap-1"
          >
            {navLinks.map((link) => {
              const active = pathname === link.href || (link.href !== '/' && pathname.startsWith(`${link.href}/`));
              return (
                <Link
                  key={link.href}
                  href={link.href}
                  data-active={active ? 'true' : undefined}
                  className={cn(
                    'group relative px-4 py-2 text-[13.5px] font-medium tracking-wide transition-colors duration-200',
                    solid
                      ? 'text-ink-secondary hover:text-ink data-[active=true]:text-ink'
                      : 'text-surface-light/75 hover:text-surface-light data-[active=true]:text-surface-light',
                  )}
                >
                  {link.label}
                  {/* animated underline */}
                  <span
                    className={cn(
                      'absolute bottom-0 left-4 right-4 h-[2px] rounded-full transition-all duration-300 origin-center',
                      active
                        ? 'scale-x-100 opacity-100'
                        : 'scale-x-0 opacity-0 group-hover:scale-x-100 group-hover:opacity-60',
                      solid ? 'bg-primary' : 'bg-accent',
                    )}
                  />
                </Link>
              );
            })}
          </nav>

          {/* ── Right actions ── */}
          <div className="hidden lg:flex items-center gap-2">
            <span
              aria-hidden
              className={cn(
                'hidden xl:inline-flex items-center h-8 px-2.5 text-[11.5px] font-medium tracking-wide rounded-md select-none',
                solid ? 'text-ink-muted' : 'text-surface-light/60',
              )}
            >
              FR&nbsp;&ndash;&nbsp;MAD
            </span>

            <div className={cn('mx-0.5 h-5 w-px', solid ? 'bg-border-subtle' : 'bg-white/20')} />

            {user ? (
              <UserMenu
                solid={solid}
                open={userMenuOpen}
                onToggle={() => setUserMenuOpen((o) => !o)}
                onClose={() => setUserMenuOpen(false)}
                initials={initials}
                firstName={user.firstName}
                lastName={user.lastName}
                email={user.email}
                onLogout={() => {
                  logout();
                  setUserMenuOpen(false);
                }}
              />
            ) : (
              <Link
                href="/auth/login"
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-[13px] font-medium transition-colors',
                  solid
                    ? 'text-ink-secondary hover:text-ink hover:bg-surface-cream'
                    : 'text-surface-light/80 hover:text-surface-light hover:bg-white/10',
                )}
              >
                <User className="h-3.5 w-3.5" />
                Connexion
              </Link>
            )}

          </div>

          {/* ── Mobile hamburger ── */}
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className={cn(
              'lg:hidden col-start-3 inline-flex h-11 w-11 items-center justify-center rounded-full transition-colors',
              solid
                ? 'text-ink hover:bg-surface-cream'
                : 'text-surface-light hover:bg-white/10',
            )}
            aria-label="Ouvrir le menu"
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </header>

      {/* Spacer for fixed nav on solid routes */}
      {!startsTransparent && <div aria-hidden className="h-[72px]" />}

      <MobileOverlay
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        user={user}
        onLogout={logout}
        initials={initials}
        pathname={pathname}
      />
    </>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// User dropdown menu
// ──────────────────────────────────────────────────────────────────────────
interface UserMenuProps {
  solid: boolean;
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  initials: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  onLogout: () => void;
}
function UserMenu({
  solid,
  open,
  onToggle,
  onClose,
  initials,
  firstName,
  lastName,
  email,
  onLogout,
}: UserMenuProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  return (
    <div className="relative ml-1">
      <button
        onClick={onToggle}
        aria-haspopup="menu"
        aria-expanded={open}
        className={cn(
          'flex items-center gap-2 rounded-full pl-1 pr-3 py-1 transition-colors border',
          solid
            ? 'border-border-subtle bg-surface hover:border-border'
            : 'border-white/20 bg-white/10 hover:bg-white/20',
        )}
      >
        <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-semibold">
          {initials || <User className="h-4 w-4" />}
        </span>
        <span
          className={cn(
            'text-sm font-medium max-w-[110px] truncate',
            solid ? 'text-ink' : 'text-surface-light',
          )}
        >
          {firstName}
        </span>
        <ChevronDown
          className={cn(
            'h-3.5 w-3.5 transition-transform duration-250',
            solid ? 'text-ink-muted' : 'text-surface-light/70',
            open && 'rotate-180',
          )}
        />
      </button>
      <AnimatePresence>
        {open && (
          <>
            <div className="fixed inset-0 z-10" onClick={onClose} aria-hidden="true" />
            <motion.div
              role="menu"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25, ease: EASE_PREMIUM }}
              className="absolute right-0 mt-2 w-60 rounded-2xl border border-border-subtle bg-surface shadow-card-hover z-20 p-1.5"
            >
              <div className="px-3 py-2.5">
                <p className="text-sm font-medium text-ink leading-tight">
                  {firstName} {lastName}
                </p>
                <p className="text-xs text-ink-muted truncate">{email}</p>
              </div>
              <div className="h-px bg-border-subtle my-1" />
              <Link
                href="/compte"
                className="flex items-center gap-2 px-3 py-2 text-sm text-ink rounded-xl hover:bg-surface-cream"
                onClick={onClose}
              >
                <Settings className="h-4 w-4 text-ink-muted" /> Mon compte
              </Link>
              <Link
                href="/compte/messages"
                className="flex items-center gap-2 px-3 py-2 text-sm text-ink rounded-xl hover:bg-surface-cream"
                onClick={onClose}
              >
                <MessageSquare className="h-4 w-4 text-ink-muted" /> Mes messages
              </Link>
              <div className="h-px bg-border-subtle my-1" />
              <button
                type="button"
                onClick={onLogout}
                className="flex w-full items-center gap-2 px-3 py-2 text-sm text-error rounded-xl hover:bg-error/10"
              >
                <LogOut className="h-4 w-4" /> Se déconnecter
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Mobile full-screen overlay
// ──────────────────────────────────────────────────────────────────────────
interface MobileOverlayProps {
  open: boolean;
  onClose: () => void;
  user: ReturnType<typeof useAuth>['user'];
  onLogout: () => void;
  initials: string;
  pathname: string;
}
function MobileOverlay({ open, onClose, user, onLogout, initials, pathname }: MobileOverlayProps) {
  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          role="dialog"
          aria-modal="true"
          aria-label="Menu de navigation"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3, ease: EASE_PREMIUM }}
          className="lg:hidden fixed inset-0 z-[60] bg-primary-900"
        >
          <div className="container-page flex h-16 items-center justify-between">
            <Link href="/" onClick={onClose} aria-label="Accueil TERRAMEZ">
              <BrandLogo variant="light" size="md" />
            </Link>
            <button
              type="button"
              onClick={onClose}
              aria-label="Fermer le menu"
              className="inline-flex h-11 w-11 items-center justify-center rounded-full text-surface-light hover:bg-white/10"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <motion.nav
            variants={staggerContainer(0.06, 0.05)}
            initial="hidden"
            animate="show"
            className="container-page mt-8 flex flex-col gap-1"
            aria-label="Liens principaux"
          >
            {navLinks.map((link) => {
              const active = pathname === link.href || (link.href !== '/' && pathname.startsWith(`${link.href}/`));
              return (
                <motion.div key={link.href} variants={fadeUp}>
                  <Link
                    href={link.href}
                    onClick={onClose}
                    data-active={active ? 'true' : undefined}
                    className={cn(
                      'block py-2.5 px-1 font-display text-[1.6rem] leading-tight text-surface-light/80',
                      'hover:text-surface-light data-[active=true]:text-accent',
                    )}
                  >
                    {link.label}
                  </Link>
                </motion.div>
              );
            })}
          </motion.nav>

          <div className="container-page absolute inset-x-0 bottom-8 space-y-3">
            {user ? (
              <div className="flex items-center justify-between gap-2">
                <Button asChild variant="glass" className="w-full flex-1" size="md">
                  <Link href="/compte" onClick={onClose}>
                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-semibold mr-2">
                      {initials}
                    </span>
                    {user.firstName}
                  </Link>
                </Button>
                <Button
                  variant="glass"
                  size="md"
                  onClick={() => {
                    onLogout();
                    onClose();
                  }}
                  aria-label="Se déconnecter"
                >
                  <LogOut className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <Button asChild variant="glass" className="w-full" size="md">
                <Link href="/auth/login" onClick={onClose}><User className="h-4 w-4 mr-2" />Connexion</Link>
              </Button>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
